import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiReferral, ApiReferralPromocode } from '@app/packages/api/v2/types/entities'
import type { V3BonusWithdrawalRequest } from '@app/packages/api/v3/types/lib'
import type { V3GetBonusesResponse } from '@app/packages/api/v3/types/responses'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi, callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

const referralPromocodeSlice = createReduxSlice<SliceState<ApiReferralPromocode | null>>('referral_promocode')
const referralsListSlice = createReduxSlice<SliceState<ApiReferral[]>>('referrals_list')
const referralBonusesSlice = createReduxSlice<SliceState<V3GetBonusesResponse>>('referral_bonuses')
const bonusWithdrawalRequestsSlice = createReduxSlice<SliceState<V3BonusWithdrawalRequest[]>>('bonus_withdrawal_requests')

export const referralPromocodeResultSelector = createSelector([referralPromocodeSlice.selector, profileUserResultSelector], (sliceState, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user || user.account_type === 'visitor') return null
    return withCheckedSliceState(c, {
      sliceState,
      slice: referralPromocodeSlice,
      hash: user.id,
      load: async dispatch => {
        const { getReferralPromocode } = await import('@app/packages/api/v2/actions/referral_promocode')
        const resp = await dispatch(callV2Api(getReferralPromocode())).catch(catchFetchDataError(dispatch, 'Referral promocode fetch failed'))
        return resp.data
      },
    })
  })
)

export const referralsListResultSelector = createSelector([referralsListSlice.selector, profileUserResultSelector], (sliceState, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user || user.account_type === 'visitor') return EMPTY_REFERRALS
    return withCheckedSliceState(c, {
      sliceState,
      slice: referralsListSlice,
      hash: user.id,
      load: async dispatch => {
        const { getReferrals } = await import('@app/packages/api/v2/actions/referrals')
        const resp = await dispatch(callV2Api(getReferrals())).catch(catchFetchDataError(dispatch, 'Referrals list fetch failed'))
        return resp.data
      },
    })
  })
)

/** The history, newest first, with the balance and when a withdrawal opens again. */
/** The referrals list reloads where it is shown: a promocode applied anywhere may have been a referral's reward. */
export const invalidateReferrals = () => referralsListSlice.remove()

export const referralBonusesResultSelector = createSelector([referralBonusesSlice.selector, profileUserResultSelector], (sliceState, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user || user.account_type === 'visitor') return NO_BONUSES
    return withCheckedSliceState(c, {
      sliceState,
      slice: referralBonusesSlice,
      hash: user.id,
      load: async dispatch => {
        const { getBonuses } = await import('@app/packages/api/v3/actions/bonuses')
        return dispatch(callApi(getBonuses())).catch(catchFetchDataError(dispatch, 'Referral bonuses fetch failed'))
      },
    })
  })
)

/** In roubles. Only a sitter earns bonuses; anyone else's is 0. */
export const bonusBalanceResultSelector = mapThunkSelector(referralBonusesResultSelector, bonuses => bonuses.meta.balance)

/** A sitter's withdrawal requests, newest first. */
export const bonusWithdrawalRequestsResultSelector = createSelector(
  [bonusWithdrawalRequestsSlice.selector, profileUserResultSelector],
  (sliceState, userResult) =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (!user || user.account_type !== 'sitter') return NO_WITHDRAWAL_REQUESTS
      return withCheckedSliceState(c, {
        sliceState,
        slice: bonusWithdrawalRequestsSlice,
        hash: user.id,
        load: async dispatch => {
          const { getBonusWithdrawalRequests } = await import('@app/packages/api/v3/actions/bonus_withdrawal_requests')
          const resp = await dispatch(callApi(getBonusWithdrawalRequests())).catch(catchFetchDataError(dispatch, 'Bonus withdrawal requests fetch failed'))
          return resp.data
        },
      })
    })
)

/** Asks for the whole balance; the requests reload, the new one pending. */
export const requestBonusWithdrawal = (attributes: { phone: string; bank: string }) =>
  createThunk(async dispatch => {
    const { postBonusWithdrawalRequests } = await import('@app/packages/api/v3/actions/bonus_withdrawal_requests')
    await dispatch(callApi(postBonusWithdrawalRequests({ data: { type: 'bonus_withdrawal_requests', attributes } })))
    dispatch(bonusWithdrawalRequestsSlice.remove())
  })

const EMPTY_REFERRALS: ApiReferral[] = []

const NO_BONUSES: V3GetBonusesResponse = { data: [], meta: { balance: 0, withdrawal_available_at: null } }

const NO_WITHDRAWAL_REQUESTS: V3BonusWithdrawalRequest[] = []
